import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AddEditProps } from '../../models/table-models';
import { Control, Controller, ControllerRenderProps, FieldError, useForm } from 'react-hook-form';
import React, { Suspense, useEffect, useState } from 'react';
import { DataOrModifiedFn, resourceCache, useAsyncResource } from 'use-async-resource';
import { AnyPassport, ConsumableContainerType, DevicePassportResources, PassportType, ShortDevicePassport } from '../../models/warehouse-models';
import { ConsumableServiceGroup, DeviceGroup, EssencePriceGroup, ReedInstallationGroup } from '../../models/configuration-models';
import { useGetConsumableServiceGroups, useGetDeviceGroups, useGetEssencePriceGroups, useGetReedInstallationGroups } from '../../utils/configuration-hooks';
import {
    addImages,
    getDevicesModel,
    postAddPassport,
    useDeletePassportImages,
    useGetAllBatteriesPassports,
    useGetAllOilPassports,
    useGetAllPassportTypes,
    useGetAllShortPassports,
    useGetAllShortDevicePassports,
    useGetPassportImages,
    useGetPassportImageUrls,
    useUpdatePassport as updatePassport,
} from '../../utils/warehouses-hooks';
import { CustomReactSelect } from '../../components/CustomReactSelect';
import { CustomButton } from '../../components/CustomButton';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { calculateConsumption } from '../../utils/helper-functions';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import noImage from '../../assets/No_image_available.png';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../../components/CustomToastContent';
import Select, { SingleValue } from 'react-select';

// Alias just to keep downstream generics readable
type AddPassportForm = AnyPassport;

const CONTAINER_TYPE_OPTIONS: { label: string; value: ConsumableContainerType }[] = [
    { label: 'BOTTLE', value: 'BOTTLE' },
    { label: 'SPRAY_CAN', value: 'SPRAY_CAN' },
];

type PassportFormFieldConfig = {
    dataKey: string;
    value: string | number | undefined | string[];
    type: string;
    prefix?: string;
    unit?: string;
    disabled?: boolean;
    required?: boolean;
    min?: number;
    step?: number;
    visible?: boolean;
};

const ViewImages = ({ id }: { id: number }) => {
    const [getImageUrls] = useAsyncResource(useGetPassportImageUrls, id);
    const [getImages] = useAsyncResource(useGetPassportImages, getImageUrls());

    return (
        <Suspense fallback={<Spinner animation='border' />}>
            <ViewImagesContent getImages={getImages} />
        </Suspense>
    );
};
const ViewImagesContent = ({
    getImages,
    otherImages,
}: {
    getImages?: DataOrModifiedFn<File[]>;
    otherImages?: File[];
}) => {
    const images = getImages ? getImages() : otherImages;
    if (images) {
        const [imageUrl] = images.map(URL.createObjectURL);
        return (
            <div className='w-50 my-3'>
                {imageUrl ? <img className='passportImage' src={imageUrl} alt='Passport preview' /> :
                    <img className='passportImage' src={noImage} alt='No preview' />}
            </div>
        );
    }
    return <></>;
};

type ChildPassportPasteResult = {
    added: string[];
    notFound: string[];
    ambiguous: string[];
};

const ChildPassportMultiSelect = ({
    oilPassports,
    field,
    placeholder,
    labels,
}: {
    oilPassports: any[] | undefined;
    field: ControllerRenderProps<any, 'childPassportIds'>;
    placeholder: string;
    labels: { added: string; notFound: string; ambiguous: string; ambiguousSuffix: string };
}) => {
    const [pasteResult, setPasteResult] = useState<ChildPassportPasteResult | null>(null);

    const selectedIds = (field.value ?? []) as number[];
    const availableChildren = (oilPassports ?? []).filter((p: any) => !p.isUniversal);
    const options = availableChildren.map((p: any) => ({ label: p.name, value: p.id }));
    const value = availableChildren
        .filter((p: any) => selectedIds.includes(p.id))
        .map((p: any) => ({ label: p.name, value: p.id }));

    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        const text = e.clipboardData.getData('text');
        if (!text) return;
        // Single token without separators -> let react-select handle as search input (autocomplete).
        if (!/[,;\n\r\t]/.test(text)) return;
        const parts = text
            .split(/[,;\n\r\t]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length === 0) return;
        e.preventDefault();
        e.stopPropagation();

        const byName = new Map<string, any[]>();
        for (const p of availableChildren) {
            const key = (p.name ?? '').trim().toLowerCase();
            if (!key) continue;
            const list = byName.get(key) ?? [];
            list.push(p);
            byName.set(key, list);
        }

        const newIds = new Set<number>(selectedIds);
        const added: string[] = [];
        const notFound: string[] = [];
        const ambiguous: string[] = [];

        for (const raw of parts) {
            const matches = byName.get(raw.toLowerCase());
            if (!matches || matches.length === 0) {
                notFound.push(raw);
            } else if (matches.length > 1) {
                ambiguous.push(raw);
            } else if (!newIds.has(matches[0].id)) {
                newIds.add(matches[0].id);
                added.push(matches[0].name);
            }
        }

        field.onChange(Array.from(newIds));
        setPasteResult({ added, notFound, ambiguous });
    };

    return (
        <div className="w-100" onPaste={handlePaste}>
            <Select
                isMulti
                className="w-100"
                placeholder={placeholder}
                options={options}
                value={value}
                onChange={(selected) => {
                    field.onChange(selected ? (selected as any[]).map((o) => o.value) : []);
                    setPasteResult(null);
                }}
            />
            {pasteResult && (pasteResult.added.length > 0 || pasteResult.notFound.length > 0 || pasteResult.ambiguous.length > 0) && (
                <div className="w-100 mt-1" style={{ fontSize: '0.85rem', lineHeight: 1.3 }}>
                    {pasteResult.added.length > 0 && (
                        <div className="text-success">
                            ✓ {labels.added} {pasteResult.added.length}: {pasteResult.added.join(', ')}
                        </div>
                    )}
                    {pasteResult.notFound.length > 0 && (
                        <div className="text-danger">
                            ⚠ {labels.notFound} {pasteResult.notFound.length}: {pasteResult.notFound.map((n) => `"${n}"`).join(', ')}
                        </div>
                    )}
                    {pasteResult.ambiguous.length > 0 && (
                        <div className="text-warning">
                            ⚠ {labels.ambiguous} {pasteResult.ambiguous.length}: {pasteResult.ambiguous.map((n) => `"${n}"`).join(', ')} {labels.ambiguousSuffix}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const OilTrackingFields = ({
    control,
    itemToEdit,
    isSprayTracking,
    t,
    renderFormField,
}: {
    control: Control<AddPassportForm>;
    itemToEdit: AnyPassport | undefined;
    isSprayTracking: boolean;
    t: (key: string) => string;
    renderFormField: (field: PassportFormFieldConfig) => React.ReactNode;
}) => (
    <>
        <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
            <Form.Label>{t('trackingType')}</Form.Label>
            <Controller
                control={control}
                name="trackingType"
                defaultValue={itemToEdit?.trackingType ?? 'LIQUID_ML'}
                render={({ field }) => {
                    const options = [
                        { label: t('LIQUID_ML'), value: 'LIQUID_ML' },
                        { label: t('SPRAY_WEIGHT'), value: 'SPRAY_WEIGHT' },
                    ];
                    return (
                        <Select
                            className="w-100"
                            options={options}
                            value={options.find((option) => option.value === field.value) ?? options[0]}
                            onChange={(option) => field.onChange(option?.value ?? 'LIQUID_ML')}
                        />
                    );
                }}
            />
        </Form.Group>
        {isSprayTracking && (
            <>
                {renderFormField({
                    dataKey: 'activeSubstanceWeightGrams',
                    value: itemToEdit?.activeSubstanceWeightGrams,
                    type: 'number',
                    unit: 'g',
                    required: true,
                })}
                {renderFormField({
                    dataKey: 'emptyPackageWeightGrams',
                    value: itemToEdit?.emptyPackageWeightGrams,
                    type: 'number',
                    unit: 'g',
                    required: true,
                })}
            </>
        )}
    </>
);

const ContainerTypeSelect = ({
    control,
    t,
    optionT,
    defaultValue,
}: {
    control: Control<AddPassportForm>;
    t: (key: string) => string;
    optionT: (key: string) => string;
    defaultValue?: ConsumableContainerType | null;
}) => (
    <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
        <Form.Label>{t('supportedContainerType')}</Form.Label>
        <Controller
            control={control}
            name={'resources.supportedContainerType' as any}
            defaultValue={defaultValue ?? null}
            render={({ field }) => (
                <Select
                    className="w-100"
                    options={CONTAINER_TYPE_OPTIONS.map((option) => ({
                        ...option,
                        label: optionT(option.value),
                    }))}
                    value={CONTAINER_TYPE_OPTIONS.map((option) => ({
                        ...option,
                        label: optionT(option.value),
                    })).find((option) => option.value === field.value) ?? null}
                    onChange={(option) => field.onChange(option?.value ?? null)}
                    isClearable
                />
            )}
        />
    </Form.Group>
);

const PassportContainerTypeSelect = ({
    control,
    t,
    optionT,
    defaultValue,
}: {
    control: Control<AddPassportForm>;
    t: (key: string) => string;
    optionT: (key: string) => string;
    defaultValue?: ConsumableContainerType | null;
}) => (
    <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
        <Form.Label>{t('containerType')}</Form.Label>
        <Controller
            control={control}
            name={'containerType' as any}
            defaultValue={defaultValue ?? null}
            render={({ field }) => (
                <Select
                    className="w-100"
                    options={CONTAINER_TYPE_OPTIONS.map((option) => ({
                        ...option,
                        label: optionT(option.value),
                    }))}
                    value={CONTAINER_TYPE_OPTIONS.map((option) => ({
                        ...option,
                        label: optionT(option.value),
                    })).find((option) => option.value === field.value) ?? null}
                    onChange={(option) => field.onChange(option?.value ?? null)}
                    isClearable
                />
            )}
        />
    </Form.Group>
);

const EmptyContainerPassportSelect = ({
    control,
    t,
    containerPassports,
    defaultValue,
}: {
    control: Control<AddPassportForm>;
    t: (key: string) => string;
    containerPassports: ShortDevicePassport[];
    defaultValue?: number | null;
}) => (
    <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
        <Form.Label>{t('emptyContainerPassport')}</Form.Label>
        <Controller
            control={control}
            name={'resources.emptyContainerPassportId' as any}
            defaultValue={defaultValue ?? null}
            render={({ field }) => (
                <Select
                    className="w-100"
                    options={containerPassports.map((passport) => ({
                        label: passport.name,
                        value: passport.id,
                    }))}
                    value={containerPassports
                        .map((passport) => ({ label: passport.name, value: passport.id }))
                        .find((option) => option.value === field.value) ?? null}
                    onChange={(option) => field.onChange(option?.value ?? null)}
                    isClearable
                />
            )}
        />
    </Form.Group>
);

export const AddEditPassport = ({ itemToEdit, setShowModal, refetchEntitiesHook }: AddEditProps<AnyPassport>) => {
    const [images, setImages] = useState<File[]>(itemToEdit?.images ?? []);
    const [hasBattery, setHasBattery] = useState(false);
    const [deviceGroupWarning, setDeviceGroupWarning] = useState<{
        formValue: AddPassportForm;
        isEdit: boolean;
    } | null>(null);
    const { t } = useTranslation('translation', { keyPrefix: 'table' });
    const { t: commonTranslation } = useTranslation('translation', { keyPrefix: 'common' });

    // Default resources.requireBattery to false when adding
    const { register, handleSubmit, formState, control, watch, resetField, setValue } = useForm<AddPassportForm>({
        mode: 'all',
        defaultValues: {
            ...itemToEdit,
            serviceable: itemToEdit?.serviceable ?? false,
            trackingType: itemToEdit?.trackingType ?? 'LIQUID_ML',
            resources: {
                ...itemToEdit?.resources,
                requireBattery: itemToEdit?.resources?.requireBattery ?? false,
                supportedContainerType: itemToEdit?.resources?.supportedContainerType ?? null,
                emptyContainerPassportId: itemToEdit?.resources?.emptyContainerPassportId ?? null,
            } as any, // keep shape; specific type fields are handled in FormFields
        },
    });
    const isUniversal = !!watch('isUniversal');
    const [readOilPassports] = useAsyncResource(useGetAllOilPassports, false);
    const oilPassports = readOilPassports();
    const [readEssencePriceGroups] = useAsyncResource(useGetEssencePriceGroups, []);
    const essencePriceGroups = readEssencePriceGroups();
    const [readDeviceGroups] = useAsyncResource(useGetDeviceGroups, []);
    const deviceGroups = readDeviceGroups();
    const [readReedInstallationGroups] = useAsyncResource(useGetReedInstallationGroups, []);
    const reedInstallationGroups = readReedInstallationGroups();
    const [readConsumableServiceGroups] = useAsyncResource(useGetConsumableServiceGroups, []);
    const consumableServiceGroups = readConsumableServiceGroups();

    const [readShortPassports] = useAsyncResource(useGetAllShortPassports, false);
    const allContainerPassports = readShortPassports().filter((passport) => ((passport.type as any)?.type ?? passport.type) === 'CONTAINER');
    const consumablePassports = readShortPassports().filter((passport) => ((passport.type as any)?.type ?? passport.type) === 'CONSUMABLE');

    const [readModels] = useAsyncResource(getDevicesModel, []);
    const models = readModels();
    const emptyOption = { name: '', value: '' };
    const selectedSupportedContainerType = watch('resources.supportedContainerType' as any);
    const selectedDeviceOilCapacity = Number(watch('resources.oilCapacity' as any) ?? itemToEdit?.resources?.oilCapacity ?? 0);
    const selectedEmptyContainerPassportId = watch('resources.emptyContainerPassportId' as any);
    const shouldShowEmptyContainerPassport = (selectedSupportedContainerType ?? itemToEdit?.resources?.supportedContainerType) === 'BOTTLE';
    const containerPassports = allContainerPassports.filter((passport) =>
        passport.containerType === 'BOTTLE' &&
        selectedDeviceOilCapacity > 0 &&
        Number(passport.capacityMl) === selectedDeviceOilCapacity
    );

    useEffect(() => {
        if (
            selectedEmptyContainerPassportId != null &&
            !containerPassports.some((passport) => passport.id === selectedEmptyContainerPassportId)
        ) {
            setValue('resources.emptyContainerPassportId' as any, null);
        }
    }, [containerPassports, selectedEmptyContainerPassportId, setValue]);

    const removeBatteryFields = (formValue: AnyPassport) => {
        if (!hasBattery && formValue.resources) {
            resetField('resources.batteryCount');
            resetField('resources.batteryModel');
            resetField('resources.batteryConsumptionOn');
            resetField('resources.batteryConsumptionStandBy');
        }
    };

    const shouldWarnForMissingDeviceGroup = (formValue: AddPassportForm) =>
        formValue.type === 'DEVICE' && !formValue.deviceGroupId;

    const clearPassportImageCache = () => {
        resourceCache(useGetPassportImageUrls).clear();
        resourceCache(useGetPassportImages).clear();
    };

    const uploadPassportImages = async (passportId: number) => {
        if (images.length === 0) {
            return;
        }
        await addImages(passportId, images);
        clearPassportImageCache();
    };

    const submitHandlerAdd = async (formValue: AddPassportForm, skipDeviceGroupWarning = false) => {
        if (!skipDeviceGroupWarning && shouldWarnForMissingDeviceGroup(formValue)) {
            setDeviceGroupWarning({ formValue, isEdit: false });
            return;
        }

        removeBatteryFields(formValue);
        let resources = { ...formValue.resources };
        if (formValue.type === 'DEVICE') {
            if (
                formValue.resources?.oilConsumption &&
                formValue.resources?.oilCapacity &&
                formValue.waitRange &&
                formValue.sprayRange
            ) {
                const { consumptionPerSecond } = calculateConsumption(
                    formValue.resources?.oilConsumption,
                    formValue.resources?.oilCapacity,
                    formValue.waitRange,
                    formValue.sprayRange,
                );
                resources = { ...formValue.resources, oilConsumptionPerSecond: consumptionPerSecond };
                postAddPassport({
                    ...formValue,
                    resources: resources as DevicePassportResources,
                }).then(async (passport) => {
                    await uploadPassportImages(passport.id);
                    refetchEntitiesHook();
                    setShowModal(false);
                    toast.success(<CustomToastContent content={[t('passportSuccess')]} />, toastOptionsTopRight);
                });
            } else toast.error(commonTranslation('fillFields'), toastOptionsTopRight);
        } else {
            if (formValue.isUniversal && (!formValue.childPassportIds || formValue.childPassportIds.length <= 0)) {
                toast.error(commonTranslation('fillFields'), toastOptionsTopRight);
                return;
            }
            postAddPassport({
                ...formValue,
                resources: resources as DevicePassportResources,
            }).then(async (passport) => {
                await uploadPassportImages(passport.id);
                refetchEntitiesHook();
                setShowModal(false);
                toast.success(<CustomToastContent content={[t('passportSuccess')]} />, toastOptionsTopRight);
            });
        }
        resourceCache(getDevicesModel).clear();
        resourceCache(useGetAllShortDevicePassports).clear();
        resourceCache(useGetAllShortPassports).clear();
        resourceCache(useGetAllOilPassports).clear();
        resourceCache(useGetAllBatteriesPassports).clear();
        resourceCache(useGetAllPassportTypes).clear();
    };

    const submitHandlerEdit = async (formValue: AddPassportForm, skipDeviceGroupWarning = false) => {
        if (!skipDeviceGroupWarning && shouldWarnForMissingDeviceGroup(formValue)) {
            setDeviceGroupWarning({ formValue, isEdit: true });
            return;
        }

        removeBatteryFields(formValue);
        await updatePassport({ ...itemToEdit, ...formValue });
        await uploadPassportImages(formValue.id);
        refetchEntitiesHook();
        setShowModal(false);
        toast.success(<CustomToastContent content={[t('passportUpdated')]} />, toastOptionsTopRight);
        resourceCache(getDevicesModel).clear();
        resourceCache(useGetAllShortDevicePassports).clear();
        resourceCache(useGetAllShortPassports).clear();
        resourceCache(useGetAllOilPassports).clear();
        resourceCache(useGetAllBatteriesPassports).clear();
        resourceCache(useGetAllPassportTypes).clear();
    };

    const confirmMissingDeviceGroupSave = () => {
        if (!deviceGroupWarning) {
            return;
        }

        const { formValue, isEdit } = deviceGroupWarning;
        setDeviceGroupWarning(null);

        if (isEdit) {
            submitHandlerEdit(formValue, true);
        } else {
            submitHandlerAdd(formValue, true);
        }
    };
    const useClearImages = async () => {
        setImages([]);
        await useDeletePassportImages(itemToEdit?.id);
        clearPassportImageCache();
    };

    const FormFields = {
        passport: [
            { value: itemToEdit?.name, type: 'text', disabled: !!itemToEdit, dataKey: 'name', required: true, visible: true },
            { type: 'number', value: itemToEdit?.price, dataKey: 'price', required: true, unit: '€', visible: true },
            { type: 'number', value: itemToEdit?.priceForInvoice, dataKey: 'priceForInvoice', required: true, unit: '€', visible: true },
            { type: 'number', value: itemToEdit?.priceForSale, dataKey: 'priceForSale', required: true, unit: '€' },
        ],
        device: [
            { type: 'text', value: itemToEdit?.model, dataKey: 'model', required: true },
            { type: 'number', value: itemToEdit?.power, dataKey: 'power', required: true, unit: 'W' },
            { type: 'number', value: itemToEdit?.areaCoverage, dataKey: 'areaCoverage', unit: 'm²', required: true },
            { type: 'number', value: itemToEdit?.resources?.oilConsumption, dataKey: 'oilConsumption', prefix: 'resources.', unit: 'ml', required: true },
            { type: 'number', value: itemToEdit?.resources?.oilCapacity, dataKey: 'oilCapacity', prefix: 'resources.', unit: 'ml', required: true, min: 1 },
            { type: 'number', value: itemToEdit?.waitRange, dataKey: 'waitRange', unit: 's', required: true },
            { type: 'number', value: itemToEdit?.sprayRange, dataKey: 'sprayRange', unit: 's', required: true },
        ],
        oil: [
            { type: 'number', value: itemToEdit?.oilCapacity, dataKey: 'oilCapacity', required: true, disabled: false, unit: 'ml' },
        ],
        battery: [
            { type: 'dropdown', value: watch('model') === undefined ? '' : watch('model'), dataKey: 'forModel', required: true },
            { type: 'number', value: itemToEdit?.batteryVoltage, dataKey: 'batteryVoltage', required: true, unit: 'V' },
            { type: 'number', value: itemToEdit?.batteryCapacity, dataKey: 'batteryCapacity', required: true, unit: 'mAh' },
        ],
        container: [
            { type: 'number', value: itemToEdit?.capacityMl, dataKey: 'capacityMl', required: true, unit: 'ml', min: 1 },
        ],
        other: [{ type: 'text', value: itemToEdit?.description, dataKey: 'description' }],
        deviceBattery: [
            { type: 'text', value: itemToEdit?.resources?.batteryModel, dataKey: 'batteryModel', prefix: 'resources.', required: true },
            { type: 'number', value: itemToEdit?.resources?.batteryCount, dataKey: 'batteryCount', prefix: 'resources.', required: true },
            { type: 'number', value: itemToEdit?.resources?.batteryConsumptionOn, unit: 'mAh', dataKey: 'batteryConsumptionOn', prefix: 'resources.', required: true },
            { type: 'number', value: itemToEdit?.resources?.batteryConsumptionStandBy, unit: 'mAh', dataKey: 'batteryConsumptionStandBy', prefix: 'resources.', required: true },
        ],
    };

    const [dynamicMin, setDynamicMin] = useState<number>(1);

    const FormField = ({
        dataKey,
        value,
        type,
        prefix,
        unit,
        disabled,
        required,
        min = 0,
        step,
        visible = true
    }: PassportFormFieldConfig) => {
        const onChangeForModel = (
            field: ControllerRenderProps<AddPassportForm, 'forModel'>,
            selectedOption: SingleValue<{ name: string; value: string }>
        ) => {
            field.onChange(selectedOption?.value);
            const selected = models.find(model => model.model === selectedOption?.value);
            const newMin = selected?.oilCapacity ?? 0;
            setValue('oilCapacity' as any, newMin as any);
            setDynamicMin(newMin);
        };
        const onChangeForMultiSelect = (
            field: ControllerRenderProps<AddPassportForm, 'forDeviceModel'>,
            selectedOptions: any,
            actionMeta: any
        ) => {
            const clickedOption = actionMeta.option;

            if (clickedOption?.value === 'All') {
                field.onChange(null);
            } else {
                if (selectedOptions.some((option: any) => option.value === 'All')) {
                    selectedOptions = selectedOptions.filter((option: any) => option.value !== 'All');
                }
                field.onChange(selectedOptions.map((option: any) => option.value));
            }
        };

        const name = prefix ? prefix + dataKey : dataKey;
        const hookRegister = register(name as keyof AddPassportForm, {
            required: {
                value: dataKey === 'forDeviceModel' ? !isUniversal : !!required,
                message: t(dataKey),
            },
            min: min,
            validate: (fieldValue) => type !== 'number' || fieldValue === '' || Number.isFinite(Number(fieldValue)),
        });

        return (
            visible && (
                <Form.Group className={'flex-column  align-items-start d-flex mb-2 w-100 '}>
                    <Form.Label>{t(dataKey)}</Form.Label>
                    <div className='d-flex w-100 justify-content-start align-items-center'>
                        <div className={`${type === 'number' && dataKey !== 'minimumDeposit' ? 'w-35' : 'w-100'}`}>
                            {type === 'dropdown' ? (
                                <Controller
                                    control={control}
                                    name='forModel'
                                    defaultValue={String(value)}
                                    render={({ field }) => (
                                        <Select
                                            className='w-35'
                                            placeholder={t('model')}
                                            options={
                                                models
                                                    ? models.map((value) =>
                                                        value
                                                            ? {
                                                                name: value?.model ?? '',
                                                                value: value?.model ?? '',
                                                            }
                                                            : emptyOption
                                                    )
                                                    : [emptyOption]
                                            }
                                            defaultValue={{ name: String(value), value: String(value) }}
                                            getOptionLabel={({ name }) => name}
                                            getOptionValue={({ value }) => value}
                                            onChange={(selectedOption) => onChangeForModel(field, selectedOption)}
                                        />
                                    )}
                                />
                            ) : type === 'multiselect' ? (
                                <Controller
                                    control={control}
                                    name='forDeviceModel'
                                    defaultValue={value as string}
                                    render={({ field }) => (
                                        <Select
                                            className='w-100'
                                            placeholder={t('forDeviceModel')}
                                            options={[
                                                { name: t('all'), value: 'All' },
                                                ...(models
                                                    ? models.map((value) =>
                                                        value
                                                            ? {
                                                                name: value?.model ?? '',
                                                                value: value?.model ?? '',
                                                            }
                                                            : emptyOption,
                                                    )
                                                    : [emptyOption]),
                                            ]}
                                            value={field.value === null || field.value === undefined ? [{
                                                name: t('all'),
                                                value: 'All'
                                            }] : Array.isArray(field.value) ? (field.value.length === 0 ? [{
                                                name: t('all'),
                                                value: 'All'
                                            }] : (field.value.map(v => ({ name: v, value: v })))) : []}
                                            getOptionLabel={({ name }) => name}
                                            getOptionValue={({ value }) => value}
                                            onChange={(selectedOptions, actionMeta) => onChangeForMultiSelect(field, selectedOptions, actionMeta)}
                                        />
                                    )}
                                />
                            ) : (
                                <Form.Control
                                    className={`${disabled ? 'disabled' : ''}`}
                                    min={dataKey === "oilCapacity" ? dynamicMin : min}
                                    step={step}
                                    disabled={disabled}
                                    //disabled={disabled ? !!itemToEdit : false}
                                    {...{ name, required, type }}
                                    {...hookRegister}
                                    defaultValue={value ?? ''}
                                />
                            )}
                            {(formState.errors[name as keyof AddPassportForm] as FieldError) && (
                                <p className={`input-error `}>*{t('required')}</p>
                            )}
                            {name.startsWith('resources') &&
                                name.substring(10).startsWith('oilCapacity') &&
                                formState.errors.resources?.[
                                name.substring(10) as keyof DevicePassportResources
                                ] && <p className={`input-error `}>*{commonTranslation('RequiredAtLeast')} {dynamicMin}</p>}
                            {name.startsWith('resources') &&
                                formState.errors.resources?.[
                                name.substring(10) as keyof DevicePassportResources
                                ] && <p className={`input-error `}>*{t('required')}</p>}
                        </div>
                        <div className='ms-2'>{unit}</div>
                    </div>
                </Form.Group>
            )
        );
    };

    const ImageAndDefaultFields = () => {
        const hidePricesForUniversalOil =
            passportType === 'OIL' && isUniversal;

        return (
            <div className='d-flex w-100'>
                <div className='d-flex flex-column flex-wrap w-50 center'>
                    {FormFields.passport.map((field, i) => {
                        let { dataKey, value, type, disabled, required, unit, visible } = field;

                        // ако е универсална OIL – скриваме цените и махаме required
                        if (
                            hidePricesForUniversalOil &&
                            (dataKey === 'price' ||
                                dataKey === 'priceForInvoice')
                        ) {
                            visible = false;
                            required = false;
                        }

                        return (
                            <React.Fragment key={`passport-field-${i}`}>
                                {FormField({ dataKey, value, type, disabled, required, unit, visible })}
                            </React.Fragment>
                        );
                    })}

                    {itemToEdit?.model && itemToEdit.type !== 'OTHER' && (
                        <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
                            <Form.Label>{t('model')}</Form.Label>
                            <Form.Control
                                value={itemToEdit.model}
                                disabled
                            />
                        </Form.Group>
                    )}
                    {showEssencePriceGroupSelector && <EssencePriceGroupSelector />}
                    {(showDeviceGroupSelector || showReedInstallationGroupSelector) && (
                        <div className='d-flex flex-wrap gap-3 w-100 align-items-start'>
                            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                {showDeviceGroupSelector ? <DeviceGroupSelector /> : <ReedInstallationGroupSelector />}
                            </div>
                            {(showDeviceGroupSelector || serviceable) && <div style={{ flex: '0 0 140px' }}>
                                {FormField({
                                    dataKey: 'minimumDeposit',
                                    value: itemToEdit?.minimumDeposit ?? 0,
                                    type: 'number',
                                    unit: '€',
                                    required: true,
                                    min: 0,
                                    step: 0.01,
                                })}
                            </div>}
                        </div>
                    )}
                    {showConsumableServiceGroupSelector && <ConsumableServiceGroupSelector />}
                </div>

                <Form.Group className='d-flex flex-column mb-2 align-items-center mt-4 w-50 '>
                    <Controller
                        name='images'
                        {...{ control }}
                        render={() => (
                            <>
                                <div className='d-flex w-100 justify-content-end'>
                                    <Form.Control
                                        type='file'
                                        className='rounded-0 w-75 flex-column'
                                        accept='.jpeg,.jpg,.png,.bmp,.gif'
                                        multiple
                                        onChange={(e: any) => setImages(Array.from(e.target.files))}
                                    />
                                    <CustomButton
                                        icon={faTrash}
                                        iconClass='fa-fw'
                                        onClick={useClearImages}
                                        className='me-1 color-main flex-column'
                                    />
                                </div>
                                {itemToEdit && images.length === 0 ? <ViewImages id={itemToEdit.id} /> : <></>}
                                {images.length > 0 ? <ViewImagesContent otherImages={images} /> : <></>}
                            </>
                        )}
                    />
                </Form.Group>
            </div>
        );
    };

    const oilConsumption = watch('resources.oilConsumption');
    const oilCapacity = watch('resources.oilCapacity');
    const waitTime = watch('waitRange');
    const sprayTime = watch('sprayRange');
    const passportType = watch('type');
    const serviceable = watch('serviceable');
    const priceForInvoice = watch('priceForInvoice');
    const showEssencePriceGroupSelector = passportType === 'OIL' && Number(priceForInvoice) > 0;
    const showDeviceGroupSelector = passportType === 'DEVICE';
    const showReedInstallationGroupSelector = passportType === 'OTHER';
    const showConsumableServiceGroupSelector = passportType === 'CONSUMABLE';
    const trackingType = watch('trackingType') ?? 'LIQUID_ML';
    const isSprayTracking = trackingType === 'SPRAY_WEIGHT';

    useEffect(() => {
        if (passportType === 'OTHER' && !serviceable) setValue('minimumDeposit', 0);
    }, [passportType, serviceable, setValue]);

    useEffect(() => {
        if (passportType === 'OIL' && isUniversal) {
            setValue('price', 0);
            setValue('priceForInvoice', 0);
            setValue('essencePriceGroupId', undefined);
        }
    }, [passportType, isUniversal, setValue]);

    useEffect(() => {
        if (!showEssencePriceGroupSelector) {
            setValue('essencePriceGroupId', undefined);
        }
    }, [showEssencePriceGroupSelector, setValue]);

    useEffect(() => {
        if (!showDeviceGroupSelector) {
            setValue('deviceGroupId', undefined);
        }
    }, [showDeviceGroupSelector, setValue]);

    useEffect(() => {
        if (!showReedInstallationGroupSelector) setValue('reedInstallationGroupId', undefined);
    }, [showReedInstallationGroupSelector, setValue]);

    useEffect(() => {
        if (!showConsumableServiceGroupSelector) setValue('consumableServiceGroupId', undefined);
    }, [showConsumableServiceGroupSelector, setValue]);

    const EssencePriceGroupSelector = () => (
        <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
            <Form.Label>{commonTranslation('essencePriceGroups')}</Form.Label>
            <Controller
                control={control}
                name="essencePriceGroupId"
                rules={{ required: showEssencePriceGroupSelector }}
                defaultValue={itemToEdit?.essencePriceGroupId}
                render={({ field }) => (
                    <Select
                        className="w-100"
                        placeholder={commonTranslation('essencePriceGroups')}
                        options={essencePriceGroups}
                        value={essencePriceGroups.find((priceGroup: EssencePriceGroup) => priceGroup.groupNumber === field.value) ?? null}
                        getOptionLabel={(priceGroup: EssencePriceGroup) => `${commonTranslation('group')} ${priceGroup.groupNumber}`}
                        getOptionValue={(priceGroup: EssencePriceGroup) => String(priceGroup.groupNumber)}
                        onChange={(priceGroup) => field.onChange(priceGroup?.groupNumber)}
                    />
                )}
            />
            {formState.errors.essencePriceGroupId && <p className={`input-error `}>*{t('required')}</p>}
        </Form.Group>
    );

    const DeviceGroupSelector = () => (
        <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
            <Form.Label>{commonTranslation('deviceGroups')}</Form.Label>
            <Controller
                control={control}
                name="deviceGroupId"
                defaultValue={itemToEdit?.deviceGroupId}
                render={({ field }) => (
                    <Select
                        className="w-100"
                        placeholder={commonTranslation('deviceGroups')}
                        options={deviceGroups}
                        value={deviceGroups.find((deviceGroup: DeviceGroup) => deviceGroup.groupNumber === field.value) ?? null}
                        getOptionLabel={(deviceGroup: DeviceGroup) => `${commonTranslation('group')} ${deviceGroup.groupNumber}`}
                        getOptionValue={(deviceGroup: DeviceGroup) => String(deviceGroup.groupNumber)}
                        onChange={(deviceGroup) => field.onChange(deviceGroup?.groupNumber)}
                        isClearable
                    />
                )}
            />
        </Form.Group>
    );

    const ReedInstallationGroupSelector = () => (
        <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
            <Form.Label>{commonTranslation('reedInstallationGroups')}</Form.Label>
            <Controller control={control} name="reedInstallationGroupId" defaultValue={itemToEdit?.reedInstallationGroupId}
                render={({ field }) => (
                    <Select className="w-100" placeholder={commonTranslation('reedInstallationGroups')}
                        options={reedInstallationGroups}
                        value={reedInstallationGroups.find((group: ReedInstallationGroup) => group.groupNumber === field.value) ?? null}
                        getOptionLabel={(group: ReedInstallationGroup) => `${commonTranslation('group')} ${group.groupNumber}`}
                        getOptionValue={(group: ReedInstallationGroup) => String(group.groupNumber)}
                        onChange={(group) => field.onChange(group?.groupNumber)} isClearable />
                )} />
        </Form.Group>
    );

    const ConsumableServiceGroupSelector = () => (
        <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
            <Form.Label>{commonTranslation('consumableServiceGroups')}</Form.Label>
            <Controller control={control} name="consumableServiceGroupId" defaultValue={itemToEdit?.consumableServiceGroupId}
                render={({ field }) => (
                    <Select className="w-100" placeholder={commonTranslation('consumableServiceGroups')}
                        options={consumableServiceGroups}
                        value={consumableServiceGroups.find((group: ConsumableServiceGroup) => group.groupNumber === field.value) ?? null}
                        getOptionLabel={(group: ConsumableServiceGroup) => `${commonTranslation('group')} ${group.groupNumber}`}
                        getOptionValue={(group: ConsumableServiceGroup) => String(group.groupNumber)}
                        onChange={(group) => field.onChange(group?.groupNumber)} isClearable />
                )} />
        </Form.Group>
    );

    const OtherServiceFields = () => (
        <div className='w-100'>
            <Form.Group className='d-flex align-items-center mb-2 w-100'>
                <Form.Check
                    id='serviceable'
                    type='switch'
                    label={commonTranslation('serviceable')}
                    {...register('serviceable')}
                />
            </Form.Group>
            {serviceable && (
                <>
                    {FormField({ dataKey: 'model', value: itemToEdit?.model, type: 'text' })}
                    <Form.Group className='flex-column align-items-start d-flex mb-2 w-100'>
                        <Form.Label>{commonTranslation('serviceConsumable')}</Form.Label>
                        <Controller
                            control={control}
                            name='serviceConsumablePassportId'
                            rules={{ required: true }}
                            render={({ field }) => (
                                <Select
                                    className='w-100'
                                    options={consumablePassports}
                                    value={consumablePassports.find((passport) => passport.id === field.value) ?? null}
                                    getOptionLabel={(passport) => passport.name}
                                    getOptionValue={(passport) => String(passport.id)}
                                    onChange={(passport) => field.onChange(passport?.id)}
                                />
                            )}
                        />
                    </Form.Group>
                </>
            )}
        </div>
    );

    const DeviceCalculations = ({
        oilConsumption,
        oilCapacity,
        waitTime,
        sprayTime,
    }: {
        oilConsumption: number;
        oilCapacity: number;
        waitTime: number;
        sprayTime: number;
    }) => {
        const { predictedWorkTime, consumptionPerSecond, secondsWait, secondsSpray } = calculateConsumption(
            oilConsumption,
            oilCapacity,
            waitTime,
            sprayTime,
        );
        return (
            <div className='d-flex w-100 flex-column h-100 justify-content-end align-items-start'>
                <div>
                    {t('sprayingSeconds')}:
                    <div className='p-2 border rounded bg-dark-gray m-3'>{secondsSpray.toFixed(0)}s</div>
                </div>
                <div>
                    {t('waitingSeconds')}:
                    <div className='p-2 border rounded bg-dark-gray m-3'> {secondsWait.toFixed(0)}s</div>
                </div>
                <div>
                    {t('predictedWorkTime')}:
                    <div className='p-2 border rounded bg-dark-gray m-3'>{predictedWorkTime.toFixed(2)}h</div>
                </div>
                <div>
                    {t('consumptionPerSecond')}:
                    <div className='p-2 border rounded bg-dark-gray m-3'>
                        {consumptionPerSecond.toFixed(4)} s +/- 10%
                    </div>
                </div>
            </div>
        );
    };
    return (
        <>
            <Form
                id='form'
                className='d-flex flex-column align-items-center p-4 w-100'
                noValidate
                onSubmit={handleSubmit((formValue) =>
                    itemToEdit ? submitHandlerEdit(formValue) : submitHandlerAdd(formValue)
                )}
            >
            {itemToEdit?.id ? (
                <>
                    {ImageAndDefaultFields()}
                    {itemToEdit.type === 'OIL' && (
                        <>
                            <Form.Group className="d-flex align-items-center mb-2 w-100">
                                <Form.Check
                                    id="isUniversal"
                                    type="checkbox"
                                    className="me-2"
                                    label={commonTranslation('universalEssences')}
                                    disabled
                                    {...register('isUniversal')}
                                />
                            </Form.Group>
                            <OilTrackingFields
                                control={control}
                                itemToEdit={itemToEdit}
                                isSprayTracking={isSprayTracking}
                                t={t}
                                renderFormField={FormField}
                            />

                            {!itemToEdit.isUniversal ? (
                                <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
                                    <Form.Label>{t('forDeviceModel')}</Form.Label>
                                    <Controller
                                        control={control}
                                        name="forDeviceModel"
                                        defaultValue={itemToEdit?.forDeviceModel ?? ''}
                                        render={({ field }) => (
                                            <Select
                                                className="w-100"
                                                isDisabled={!!itemToEdit?.forDeviceModel}
                                                placeholder={t('deviceModel')}
                                                options={models ? models.map((m) => ({ label: m.model ?? '', value: m.model ?? '' })) : []}
                                                value={field.value ? { label: field.value as string, value: field.value as string } : null}
                                                onChange={(option) => field.onChange(option ? option.value : '')}
                                            />
                                        )}
                                    />
                                </Form.Group>
                            ) : (
                                <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
                                    <Form.Label>{t('childPassports')}</Form.Label>
                                    <Controller
                                        control={control}
                                        name="childPassportIds"
                                        defaultValue={itemToEdit?.childPassports?.map((p: any) => p.id) ?? []}
                                        render={({ field }) => (
                                            <ChildPassportMultiSelect
                                                oilPassports={oilPassports}
                                                field={field as any}
                                                placeholder={t('childPassports')}
                                                labels={{
                                                    added: t('pasteAdded'),
                                                    notFound: t('pasteNotFound'),
                                                    ambiguous: t('pasteAmbiguous'),
                                                    ambiguousSuffix: t('pasteAmbiguousSuffix'),
                                                }}
                                            />
                                        )}
                                    />
                                </Form.Group>
                            )}

                            {FormFields.oil.map(({ dataKey, value, type, unit, required, disabled }, i) => (
                                <React.Fragment key={`oil-field-${i}`}>
                                    {FormField({ dataKey, value, type, unit, required, disabled })}
                                </React.Fragment>
                            ))}
                        </>
                    )}
                    {itemToEdit.type === 'DEVICE' && (
                        <>
                            <ContainerTypeSelect
                                control={control}
                                t={t}
                                optionT={commonTranslation}
                                defaultValue={itemToEdit.resources?.supportedContainerType}
                            />
                            {shouldShowEmptyContainerPassport && (
                                <EmptyContainerPassportSelect
                                    control={control}
                                    t={t}
                                    containerPassports={containerPassports}
                                    defaultValue={itemToEdit.resources?.emptyContainerPassportId}
                                />
                            )}
                        </>
                    )}
                    {itemToEdit.type === 'OTHER' && (
                        <OtherServiceFields />
                    )}
                </>
            ) : (
                <>
                    <Form.Group className='d-flex flex-column mb-2 align-items-start w-100 '>
                        <Form.Label>{t('type')}</Form.Label>
                        <Controller
                            control={control}
                            name='type'
                            rules={{ required: true }}
                            defaultValue={itemToEdit?.type}
                            render={({ field }) => (
                                <>
                                    <CustomReactSelect
                                        translated
                                        defaultValue={field.value}
                                        itemsToShow={PassportType}
                                        updateValue={(value) => {
                                            field.onChange(value);
                                            setDynamicMin(50);
                                            resetField('oilCapacity');
                                        }}
                                    />
                                    {formState.errors.type && (
                                        <p className={`input-error`}>{'*' + t('required')}</p>
                                    )}
                                </>
                            )}
                        />
                    </Form.Group>
                    {ImageAndDefaultFields()}
                    {passportType === 'DEVICE' && (
                        <>
                            <h4>{commonTranslation('device')}</h4>
                            <div className='w-100 d-flex justify-content-between'>
                                <div className='w-50'>
                                    {FormFields.device.map(({
                                        dataKey,
                                        value,
                                        type,
                                        prefix,
                                        unit,
                                        required,
                                        min,
                                    }, i) => (
                                        <React.Fragment key={`device-field-${i}`}>
                                            {FormField({ dataKey, value, type, prefix, unit, required, min })}
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div className='w-50'>
                                    {oilConsumption && oilCapacity && sprayTime && waitTime && (
                                        <DeviceCalculations {...{ oilConsumption, oilCapacity, sprayTime, waitTime }} />
                                    )}
                                </div>
                            </div>

                            <ContainerTypeSelect
                                control={control}
                                t={t}
                                optionT={commonTranslation}
                            />
                            {shouldShowEmptyContainerPassport && (
                                <EmptyContainerPassportSelect
                                    control={control}
                                    t={t}
                                    containerPassports={containerPassports}
                                />
                            )}

                            <Form.Group>
                                <Form.Label className='d-flex justify-content-start w-100 align-items-center'>
                                    <h5>{commonTranslation('battery')}</h5>
                                    <Form.Check
                                        className='m-4'
                                        type='switch'
                                        checked={hasBattery}
                                        onChange={(event) => setHasBattery(event.currentTarget.checked)}
                                    />
                                </Form.Label>
                            </Form.Group>
                            <div className='w-100 align-items-start'>
                                {hasBattery && (
                                    <div className="mt-2 pb-4 d-flex align-items-center"
                                        style={{ paddingLeft: '0.25rem' }}>
                                        <Form.Check.Input
                                            id="resources.requireBattery"
                                            type="checkbox"
                                            className="form-check-input position-static me-2"
                                            style={{ transform: 'scale(1.1)', transformOrigin: 'center' }}
                                            {...register('resources.requireBattery')}
                                        />
                                        <Form.Check.Label htmlFor="resources.requireBattery" className="mb-0">
                                            {commonTranslation('addBattery')}
                                        </Form.Check.Label>
                                    </div>
                                )}
                                <div className={'w-50'}>
                                    {hasBattery &&
                                        FormFields.deviceBattery.map(({
                                            dataKey,
                                            value,
                                            type,
                                            prefix,
                                            unit,
                                            required,
                                        }, i) => (
                                            <React.Fragment key={`device-battery-field-${i}`}>
                                                {FormField({ dataKey, value, type, prefix, unit, required })}
                                            </React.Fragment>
                                        ))}
                                </div>
                            </div>
                        </>
                    )}
                    {passportType === 'OIL' && (
                        <>
                            <h4>{commonTranslation('oil')}</h4>

                            {/* ЧЕКБОКС: универсална есенция */}
                            <Form.Group className="d-flex align-items-center mb-2 w-100">
                                <Form.Check
                                    id="isUniversal"
                                    type="checkbox"
                                    className="me-2"
                                    label={commonTranslation('universalEssences') /* добави превод */}
                                    {...register('isUniversal')}
                                />
                            </Form.Group>
                            <OilTrackingFields
                                control={control}
                                itemToEdit={itemToEdit}
                                isSprayTracking={isSprayTracking}
                                t={t}
                                renderFormField={FormField}
                            />

                            {/* Ако НЕ е универсална → избираме ЕДИН модел устройство */}
                            {!isUniversal ? (
                                <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
                                    <Form.Label>{t('forDeviceModel')}</Form.Label>
                                    <Controller
                                        control={control}
                                        name="forDeviceModel"
                                        defaultValue={itemToEdit?.forDeviceModel ?? ''}
                                        render={({ field }) => (
                                            <Select
                                                className="w-100"
                                                placeholder={t('deviceModel')}
                                                options={
                                                    models
                                                        ? models.map((m) => ({
                                                            label: m.model ?? '',
                                                            value: m.model ?? '',
                                                        }))
                                                        : []
                                                }
                                                value={
                                                    field.value
                                                        ? { label: field.value as string, value: field.value as string }
                                                        : null
                                                }
                                                onChange={(option) => field.onChange(option ? option.value : '')}
                                            />
                                        )}
                                    />
                                </Form.Group>
                            ) : (

                                <Form.Group className="flex-column align-items-start d-flex mb-2 w-100">
                                    <Form.Label>{t('childPassports')}</Form.Label>
                                    <Controller
                                        control={control}
                                        name="childPassportIds"
                                        defaultValue={itemToEdit?.childPassports?.map((p: any) => p.id) ?? []}
                                        render={({ field }) => (
                                            <ChildPassportMultiSelect
                                                oilPassports={oilPassports}
                                                field={field as any}
                                                placeholder={t('childPassports')}
                                                labels={{
                                                    added: t('pasteAdded'),
                                                    notFound: t('pasteNotFound'),
                                                    ambiguous: t('pasteAmbiguous'),
                                                    ambiguousSuffix: t('pasteAmbiguousSuffix'),
                                                }}
                                            />
                                        )}
                                    />
                                </Form.Group>
                            )}

                            {/* Оставяме капацитета да се рендерира през FormField, както преди */}
                            {FormFields.oil.map(({ dataKey, value, type, unit, required, disabled }, i) => (
                                <React.Fragment key={`oil-field-${i}`}>
                                    {FormField({ dataKey, value, type, unit, required, disabled })}
                                </React.Fragment>
                            ))}
                        </>
                    )}

                    {passportType === 'CONTAINER' && (
                        <>
                            <h4>{t('CONTAINER')}</h4>
                            <PassportContainerTypeSelect
                                control={control}
                                t={t}
                                optionT={commonTranslation}
                            />
                            {FormFields.container.map(({ dataKey, value, type, unit, required, min }, i) => (
                                <React.Fragment key={`container-field-${i}`}>
                                    {FormField({ dataKey, value, type, unit, required, min })}
                                </React.Fragment>
                            ))}
                        </>
                    )}
                    {passportType === 'BATTERY' && (
                        <>
                            <h4>{commonTranslation('battery')}</h4>
                            {FormFields.battery.map(({ dataKey, value, type, unit, required }, i) => (
                                <React.Fragment key={`battery-field-${i}`}>
                                    {FormField({ dataKey, value, type, unit, required })}
                                </React.Fragment>
                            ))}
                        </>
                    )}
                    {passportType === 'OTHER' && (
                        <>
                            <h4>{commonTranslation('other')}</h4>
                            {FormFields.other.map(({ dataKey, value, type }, i) => (
                                <React.Fragment key={`other-field-${i}`}>
                                    {FormField({ dataKey, value, type })}
                                </React.Fragment>
                            ))}
                            <OtherServiceFields />
                        </>
                    )}
                </>
            )}
            </Form>
            <Modal show={!!deviceGroupWarning} onHide={() => setDeviceGroupWarning(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Внимание</Modal.Title>
                </Modal.Header>
                <Modal.Body>Устройството не участва в група. Желаете ли да продължите със записа?</Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setDeviceGroupWarning(null)}>
                        Назад
                    </Button>
                    <Button variant='primary' onClick={confirmMissingDeviceGroupSave}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
